/**
 * Shared paginated-list hook. Wraps useQuery with page/pageSize/sort/
 * search state, debounces the search input, syncs with the URL.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiFetch } from './api.ts';

interface PaginatedResponse<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ListState {
  page: number;
  pageSize: number;
  search: string;
  sortColumn: string;
  sortDirection: 'ASC' | 'DESC';
  filters: Record<string, string>;
}

export interface UseListResult<T> {
  state: ListState;
  data: PaginatedResponse<T> | undefined;
  isLoading: boolean;
  error: unknown;
  setPage: (page: number) => void;
  setSearch: (s: string) => void;
  toggleSort: (column: string) => void;
  setFilter: (key: string, value: string) => void;
}

export function usePaginatedList<T>(opts: {
  endpoint: string;
  defaultSortColumn: string;
  defaultSortDirection?: 'ASC' | 'DESC';
  pageSize?: number;
  /** Extra query keys to track in URL/state. */
  filterKeys?: string[];
}): UseListResult<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: Record<string, string> = {};
  for (const k of opts.filterKeys ?? []) {
    const v = searchParams.get(k);
    if (v) filters[k] = v;
  }

  const state: ListState = {
    page: Math.max(1, Number(searchParams.get('page') ?? 1)),
    pageSize: opts.pageSize ?? 50,
    search: searchParams.get('q') ?? '',
    sortColumn: searchParams.get('sort')?.split(':')[0] ?? opts.defaultSortColumn,
    sortDirection:
      (searchParams.get('sort')?.split(':')[1]?.toUpperCase() as
        | 'ASC'
        | 'DESC'
        | undefined) ?? opts.defaultSortDirection ?? 'DESC',
    filters,
  };

  const [searchInput, setSearchInput] = useState(state.search);
  // Debounce search → URL. Combine the q + page resets into a SINGLE
  // setSearchParams call — react-router-dom v6 doesn't batch sequential
  // setSearchParams updates (the second call's `prev` still reflects
  // pre-first-call state), so doing two calls back-to-back loses the
  // first's changes.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput !== state.search) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('q', searchInput);
          else next.delete('q');
          next.set('page', '1');
          return next;
        });
      }
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  function setQueryParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === '' || value == null) next.delete(key);
      else next.set(key, value);
      return next;
    });
  }

  const queryParams = new URLSearchParams();
  queryParams.set('page', String(state.page));
  queryParams.set('pageSize', String(state.pageSize));
  queryParams.set('sort', `${state.sortColumn}:${state.sortDirection.toLowerCase()}`);
  if (state.search) queryParams.set('q', state.search);
  for (const [k, v] of Object.entries(state.filters)) {
    queryParams.set(k, v);
  }
  const url = `${opts.endpoint}?${queryParams.toString()}`;

  const { data, isLoading, error } = useQuery({
    queryKey: [opts.endpoint, state.page, state.pageSize, state.sortColumn, state.sortDirection, state.search, JSON.stringify(state.filters)],
    queryFn: () => apiFetch<PaginatedResponse<T>>(url),
    placeholderData: (prev) => prev,
  });

  return {
    state,
    data,
    isLoading,
    error,
    setPage: (page) => setQueryParam('page', String(page)),
    setSearch: (s) => setSearchInput(s),
    toggleSort: (column) => {
      const newDir =
        state.sortColumn === column && state.sortDirection === 'DESC' ? 'ASC' : 'DESC';
      setQueryParam('sort', `${column}:${newDir.toLowerCase()}`);
    },
    setFilter: (key, value) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === '' || value == null) next.delete(key);
        else next.set(key, value);
        next.set('page', '1');
        return next;
      });
    },
  };
}

/** Convenience reset for the search input — exposed so list pages can wire
 * up an "X" clear button without re-implementing debouncing. */
export function useDebouncedSearchInput(initial: string, onCommit: (s: string) => void) {
  const [value, setValue] = useState(initial);
  useEffect(() => {
    const h = setTimeout(() => onCommit(value), 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return [value, setValue] as const;
}
